import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import './index.css';

// Public pages
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import PlansPage from './pages/public/PlansPage';
import ContactPage from './pages/public/ContactPage';
import TermsPage from './pages/public/TermsPage';
import PrivacyPage from './pages/public/PrivacyPage';

// Auth pages
import RegisterPage from './pages/auth/RegisterPage';
import LoginPage from './pages/auth/LoginPage';
import PaymentPage from './pages/auth/PaymentPage';

// Business Dashboard
import BusinessDashboard from './pages/business/Dashboard';
import BusinessChats from './pages/business/Chats';
import BusinessAgents from './pages/business/Agents';
import Leads from './pages/business/Leads';
import CRM from './pages/business/CRM';
import Tickets from './pages/business/Tickets';
import AIBot from './pages/business/AIBot';
import APIAccess from './pages/business/APIAccess';
import ContactForm from './pages/business/ContactForm';
import BusinessBranding from './pages/business/Branding';
import BusinessWidget from './pages/business/Widget';
import BusinessAnalytics from './pages/business/Analytics';
import BusinessBilling from './pages/business/Billing';
import BusinessSettings from './pages/business/Settings';

// Agent Dashboard
import AgentDashboard from './pages/agent/Dashboard';
import AgentChats from './pages/agent/Chats';
import AgentLeads from './pages/agent/Leads';
import AgentCRM from './pages/agent/CRM';
import AgentProfile from './pages/agent/Profile';

// Super Admin Dashboard
import SuperAdminDashboard from './pages/superadmin/Dashboard';
import SuperAdminBusinesses from './pages/superadmin/Businesses';
import SuperAdminPayments from './pages/superadmin/Payments';
import SuperAdminPlans from './pages/superadmin/Plans';
import SuperAdminAgents from './pages/superadmin/Agents';
import AdminTickets from './pages/superadmin/Tickets';
import AdminReports from './pages/superadmin/Reports';
import InvoiceReport from './pages/superadmin/InvoiceReport';

// Protected route - requires login
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, role, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#000', color: '#00d55b', fontSize: 15, flexDirection: 'column', gap: 12 }}>
      <div style={{ width: 36, height: 36, border: '3px solid #1e1e1e', borderTop: '3px solid #00d55b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      Loading MSChat...
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(role)) {
    // Redirect to correct home based on actual role instead of login
    if (role === 'super_admin') return <Navigate to="/super-admin" replace />;
    if (role === 'agent' || role === 'main_agent') return <Navigate to="/agent" replace />;
    if (role === 'business') return <Navigate to="/dashboard" replace />;
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Payment route - accessible without auth (for new registrations)
const PaymentRoute = ({ children }) => {
  const { loading } = useAuth();
  if (loading) return null;
  // Always allow access to payment page - user may not be logged in yet after registration
  return children;
};

// Load widget only on public pages
function PublicWidget() {
  const location = window.location || {};
  const path = window.location.pathname;
  const isPublic = !path.startsWith('/dashboard') && 
                   !path.startsWith('/agent') && 
                   !path.startsWith('/super-admin') &&
                   path !== '/login' &&
                   path !== '/register' &&
                   path !== '/payment';
  
  React.useEffect(() => {
    if (!isPublic) return;
    // Remove existing widget if any
    const existing = document.getElementById('msc-widget-script');
    if (existing) return;
    const script = document.createElement('script');
    script.id = 'msc-widget-script';
    script.src = 'https://mschat.msdiscoveries.com/widget.js';
    script.setAttribute('data-bot-id', 'MSDISCOVERIES_BOT_ID');
    document.body.appendChild(script);
    return () => {
      // Cleanup widget on unmount
      const s = document.getElementById('msc-widget-script');
      if (s) s.remove();
      const w = document.getElementById('msc');
      if (w) w.remove();
    };
  }, [isPublic]);
  return null;
}

function RoleRedirect() {
  const { user, role } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (role === 'super_admin') return <Navigate to="/super-admin" replace />;
  if (role === 'agent' || role === 'main_agent') return <Navigate to="/agent" replace />;
  if (role === 'business') return <Navigate to="/dashboard" replace />;
  return <Navigate to="/login" replace />;
}

function AppRoutes() {
  return (
    <>
    <PublicWidget />
    <Routes>
      {/* Public */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/plans" element={<PlansPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Auth */}
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/login" element={<LoginPage />} />
      {/* Payment is accessible without login - new registrations need it */}
      <Route path="/payment" element={<PaymentRoute><PaymentPage /></PaymentRoute>} />

      {/* Business Admin */}
      <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['business']}><BusinessDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/chats" element={<ProtectedRoute allowedRoles={['business']}><BusinessChats /></ProtectedRoute>} />
      <Route path="/dashboard/agents" element={<ProtectedRoute allowedRoles={['business']}><BusinessAgents /></ProtectedRoute>} />
      <Route path="/dashboard/leads" element={<ProtectedRoute allowedRoles={['business']}><Leads /></ProtectedRoute>} />
      <Route path="/dashboard/crm" element={<ProtectedRoute allowedRoles={['business']}><CRM /></ProtectedRoute>} />
      <Route path="/dashboard/tickets" element={<ProtectedRoute allowedRoles={['business']}><Tickets /></ProtectedRoute>} />
      <Route path="/dashboard/ai-bot" element={<ProtectedRoute allowedRoles={['business']}><AIBot /></ProtectedRoute>} />
      <Route path="/dashboard/api" element={<ProtectedRoute allowedRoles={['business']}><APIAccess /></ProtectedRoute>} />
      <Route path="/dashboard/branding" element={<ProtectedRoute allowedRoles={['business']}><BusinessBranding /></ProtectedRoute>} />
      <Route path="/dashboard/widget" element={<ProtectedRoute allowedRoles={['business']}><BusinessWidget /></ProtectedRoute>} />
      <Route path="/dashboard/analytics" element={<ProtectedRoute allowedRoles={['business']}><BusinessAnalytics /></ProtectedRoute>} />
      <Route path="/dashboard/billing" element={<ProtectedRoute allowedRoles={['business']}><BusinessBilling /></ProtectedRoute>} />
      <Route path="/dashboard/contact-form" element={<ProtectedRoute allowedRoles={['business']}><ContactForm /></ProtectedRoute>} />
      <Route path="/dashboard/settings" element={<ProtectedRoute allowedRoles={['business']}><BusinessSettings /></ProtectedRoute>} />

      {/* Agent */}
      <Route path="/agent" element={<ProtectedRoute allowedRoles={['agent', 'main_agent']}><AgentDashboard /></ProtectedRoute>} />
      <Route path="/agent/chats" element={<ProtectedRoute allowedRoles={['agent', 'main_agent']}><AgentChats /></ProtectedRoute>} />
      <Route path="/agent/leads" element={<ProtectedRoute allowedRoles={['agent', 'main_agent']}><AgentLeads /></ProtectedRoute>} />
      <Route path="/agent/crm" element={<ProtectedRoute allowedRoles={['agent', 'main_agent']}><AgentCRM /></ProtectedRoute>} />
      <Route path="/agent/profile" element={<ProtectedRoute allowedRoles={['agent', 'main_agent']}><AgentProfile /></ProtectedRoute>} />

      {/* Super Admin */}
      <Route path="/super-admin" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminDashboard /></ProtectedRoute>} />
      <Route path="/super-admin/businesses" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminBusinesses /></ProtectedRoute>} />
      <Route path="/super-admin/payments" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminPayments /></ProtectedRoute>} />
      <Route path="/super-admin/plans" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminPlans /></ProtectedRoute>} />
      <Route path="/super-admin/tickets" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminTickets /></ProtectedRoute>} />
      <Route path="/super-admin/invoice-report" element={<ProtectedRoute allowedRoles={['super_admin']}><InvoiceReport /></ProtectedRoute>} />
      <Route path="/super-admin/reports" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminReports /></ProtectedRoute>} />
      <Route path="/super-admin/agents" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminAgents /></ProtectedRoute>} />

      <Route path="*" element={<RoleRedirect />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" toastOptions={{
          style: { background: '#111', color: '#fff', border: '1px solid #1e1e1e', fontFamily: 'Sora, sans-serif', fontSize: 13 },
          success: { iconTheme: { primary: '#00d55b', secondary: '#000' } },
          error: { iconTheme: { primary: '#ff4444', secondary: '#fff' } }
        }} />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}